import React, { useState, useEffect, useCallback } from "react";
import { collection, getDocs, doc, setDoc, updateDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../utils/firebase";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Chip from "@mui/material/Chip";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import { Lock, Unlock, Send, Link2 } from "lucide-react";
import SchemaFormRenderer from "./SchemaFormRenderer";
import { applySchemaDefaults } from './schemaDefaults';
import { cleanConfigurationSnapshot, configurationSnapshot } from './configurationHistory';
import { saveConfigurationRevision } from './saveConfigurationRevision';
import { dataFieldWarning as getDataFieldWarning } from './dataFieldWarnings';
import { initializeFieldBinding } from './dataFieldBinding';

// Helper to convert display name to camelCase ID
const toCamelCase = (str) => {
  if (!str) return "";
  return str
    .replace(/[^a-zA-Z0-9\s]/g, "") // Remove special chars
    .split(/\s+/)
    .filter(Boolean)
    .map((word, index) => {
      if (index === 0) {
        return word.toLowerCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join("");
};

// Helper to convert display name to snake_case field name
const toSnakeCase = (str) => {
  if (!str) return "";
  return str
    .replace(/[^a-zA-Z0-9\s]/g, "") // Remove special chars
    .split(/\s+/)
    .filter(Boolean)
    .map(word => word.toLowerCase())
    .join("_");
};

/**
 * Reusable dialog for creating/editing Steps and Sections
 * Used by both AdminSchemaBasedEditor and AdminDocumentTypeEditor
 */
const ItemFormDialog = ({ open, onClose, editingItem, currentUser, config, schema, onSave }) => {
  const [id, setId] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [identityFieldValue, setIdentityFieldValue] = useState("");
  const [idManuallyEdited, setIdManuallyEdited] = useState(false);
  const [identityFieldManuallyEdited, setIdentityFieldManuallyEdited] = useState(false);
  const [identityFieldUnlocked, setIdentityFieldUnlocked] = useState(false);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});
  
  // Tabs and versioning state
  const [activeTab, setActiveTab] = useState(0); // 0 = Edit, 1 = Versions
  const [sessionUnlocked, setSessionUnlocked] = useState(false);
  const [unlockDialogOpen, setUnlockDialogOpen] = useState(false);
  
  // Data field tracking for steps/sections
  const [dataFieldsRegistry, setDataFieldsRegistry] = useState([]);
  const [unpairedFields, setUnpairedFields] = useState([]);
  const [selectedUnpairedField, setSelectedUnpairedField] = useState(null);
  const [fieldWriters, setFieldWriters] = useState(null);
  const [fieldSelectionChanged, setFieldSelectionChanged] = useState(false);
  
  // Check if this is a step or section collection (needs data field handling)
  const isStepCollection = config.collectionName === "documentSteps";
  const isSectionCollection = config.collectionName === "documentSections";
  const needsDataFieldHandling = isStepCollection || isSectionCollection;
  
  // Version/publish status derived from editingItem (skip if versioning disabled)
  const isLocked = !config.disableVersioning && editingItem?.isLocked && !sessionUnlocked;
  const hasUnpublishedChanges = !config.disableVersioning && editingItem && (
    !editingItem.publishedVersion || editingItem.version !== editingItem.publishedVersion
  );

  // Find the identity field from schema (marked with identityField: true)
  const identityField = schema?.fields?.find(f => f.identityField);
  const hasPriority = schema?.fields?.some(f => f.key === 'priority');
  
  // Check if schema specifies a primary display field (e.g., "title" for sections)
  const primaryDisplayFieldKey = schema?.primaryDisplayField;
  const primaryDisplayField = primaryDisplayFieldKey 
    ? schema?.fields?.find(f => f.key === primaryDisplayFieldKey) 
    : null;

  const displayedFormData = needsDataFieldHandling && !fieldSelectionChanged
    ? initializeFieldBinding(formData, dataFieldsRegistry, isStepCollection)
    : formData;
  const dataFieldWarning = needsDataFieldHandling ? getDataFieldWarning({
    fieldName: isStepCollection ? displayedFormData.output : displayedFormData.dataField,
    fields: dataFieldsRegistry,
    steps: fieldWriters,
    isStep: isStepCollection,
    editingId: editingItem?.id,
  }) : null;

  // Fetch data fields registry for steps/sections
  useEffect(() => {
    let cancelled = false;
    setFieldWriters(null);
    const fetchDataFields = async () => {
      if (!needsDataFieldHandling || !open) return;
      try {
        const [snapshot, stepsSnapshot] = await Promise.all([
          getDocs(collection(db, "documentDataFields")),
          getDocs(collection(db, "documentSteps")),
        ]);
        if (cancelled) return;
        const fields = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setDataFieldsRegistry(fields);
        setFieldWriters(stepsSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })));
        
        // Find unpaired fields created in the last 3 days
        if (!editingItem) {
          const threeDaysAgo = new Date();
          threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
          
          let unpaired;
          if (isSectionCollection) {
            // For sections: fields with writer but no reader
            unpaired = fields.filter(f => 
              f.usedBySteps?.length > 0 && (!f.usedBySections || f.usedBySections.length === 0)
            );
          } else if (isStepCollection) {
            // For steps: fields with reader but no writer
            unpaired = fields.filter(f => 
              f.usedBySections?.length > 0 && (!f.usedBySteps || f.usedBySteps.length === 0)
            );
          }
          
          if (unpaired) {
            // Filter to last 3 days and sort by createdAt descending
            unpaired = unpaired
              .filter(f => {
                if (!f.createdAt) return false;
                const createdDate = f.createdAt.toDate ? f.createdAt.toDate() : new Date(f.createdAt.seconds * 1000);
                return createdDate >= threeDaysAgo;
              })
              .sort((a, b) => {
                const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt?.seconds * 1000 || 0);
                const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt?.seconds * 1000 || 0);
                return bDate - aDate; // Most recent first
              })
              .slice(0, 3); // Limit to 3
            
            setUnpairedFields(unpaired);
          }
        }
      } catch (error) {
        console.error("Error fetching data fields registry:", error);
      }
    };
    fetchDataFields();
    return () => { cancelled = true; };
  }, [open, needsDataFieldHandling, isSectionCollection, isStepCollection, editingItem]);

  // Initialize form when dialog opens
  useEffect(() => {
    if (editingItem) {
      console.log('[ItemFormDialog] Initializing with editingItem:', editingItem);
      setId(editingItem.id || "");
      // If using primaryDisplayField, displayName comes from that field value
      if (primaryDisplayFieldKey) {
        setDisplayName(editingItem[primaryDisplayFieldKey] || "");
      } else {
        setDisplayName(editingItem.displayName || editingItem.name || editingItem.title || "");
      }
      // For identity field, use the field value or fall back to document ID (for legacy records)
      setIdentityFieldValue(identityField ? (editingItem[identityField.key] || editingItem.id || "") : "");
      setIdManuallyEdited(true); // Existing items have fixed IDs
      setIdentityFieldManuallyEdited(true); // Existing items have fixed identity fields
      const { id: _id, displayName: _dn, name: _n, createdAt, updatedAt, updatedBy, ...rest } = editingItem;
      console.log('[ItemFormDialog] Setting formData to:', rest);
      setFormData(needsDataFieldHandling ? applySchemaDefaults(schema, rest) : rest);
    } else {
      setId("");
      setDisplayName("");
      setIdentityFieldValue("");
      setIdManuallyEdited(false);
      setIdentityFieldManuallyEdited(false);
      // Initialize with defaults from schema
      setFormData(applySchemaDefaults(schema));
    }
    setError("");
    setErrors({});
    setActiveTab(0); // Reset to Edit tab
    setSessionUnlocked(false); // Reset unlock state
    setIdentityFieldUnlocked(false); // Reset identity field unlock state
    setSelectedUnpairedField(null);
    setFieldSelectionChanged(false);
  }, [editingItem, open, schema, primaryDisplayFieldKey, identityField, needsDataFieldHandling]);

  // Handle form field changes
  const handleFormChange = useCallback((key, value) => {
    if (key === 'output' || key === 'dataField') setFieldSelectionChanged(true);
    console.log('[ItemFormDialog] Form change:', key, value);
    setFormData(prev => {
      const next = { ...prev };
      const parts = key.split('.');
      let current = next;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!current[parts[i]]) current[parts[i]] = {};
        current = current[parts[i]];
      }
      current[parts[parts.length - 1]] = value;
      return next;
    });
  }, []);

  // Handle display name change with auto-ID and auto-identity field generation
  const handleDisplayNameChange = (value) => {
    setDisplayName(value);
    setSelectedUnpairedField(null); // Clear unpaired selection when typing
    
    // If using primaryDisplayField, also update formData
    if (primaryDisplayFieldKey) {
      handleFormChange(primaryDisplayFieldKey, value);
    }
    if (!editingItem && !idManuallyEdited) {
      setId(toCamelCase(value));
    }
    if (!editingItem && !identityFieldManuallyEdited && identityField) {
      // Use the format specified in the schema
      if (identityField.autoFormat === 'snake_case') {
        setIdentityFieldValue(toSnakeCase(value));
      } else if (identityField.autoFormat === 'camelCase') {
        setIdentityFieldValue(toCamelCase(value));
      }
    }
    
  };

  // Handle selection of unpaired field
  const handleUnpairedFieldSelect = (field) => {
    setSelectedUnpairedField(field);
    handleFormChange(isStepCollection ? 'output' : 'dataField', field.fieldName || field.id);
    setDisplayName(field.displayName);
    if (primaryDisplayFieldKey) {
      handleFormChange(primaryDisplayFieldKey, field.displayName);
    }
    if (!idManuallyEdited) {
      setId(toCamelCase(field.displayName));
    }
  };

  // Check if a field should be visible based on showWhen condition
  const isFieldVisible = (showWhen) => {
    if (!showWhen) return true;
    
    const fieldValue = getNestedValue(displayedFormData, showWhen.field);
    
    if ("equals" in showWhen) {
      return fieldValue === showWhen.equals;
    }
    if ("notEquals" in showWhen) {
      return fieldValue !== showWhen.notEquals;
    }
    if ("in" in showWhen) {
      return showWhen.in.includes(fieldValue);
    }
    
    return true;
  };

  // Validate required fields
  const validateForm = () => {
    const newErrors = {};
    
    // Skip ID validation when document ID is hidden (uses Firestore auto-IDs)
    // Also skip ID format validation when editing existing items (ID is already set and immutable)
    if (!config.hideDocumentId && !editingItem) {
      if (!id.trim()) {
        newErrors._id = "ID is required";
      } else if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(id.trim())) {
        newErrors._id = "ID must start with a letter and contain only letters, numbers, and underscores";
      }
    }
    
    if (!displayName.trim()) {
      const fieldLabel = primaryDisplayField?.label || "Display name";
      newErrors._displayName = `${fieldLabel} is required`;
    }

    if (schema?.fields) {
      schema.fields.forEach(field => {
        // Skip validation for primaryDisplayField since it's handled above
        if (field.key === primaryDisplayFieldKey) return;
        // Skip hidden fields - they are auto-generated
        if (field.hidden) return;
        // Skip fields that are not visible due to showWhen condition
        if (!isFieldVisible(field.showWhen)) return;
        if (field.required) {
          const value = getNestedValue(displayedFormData, field.key);
          if (value === undefined || value === null || value === "") {
            newErrors[field.key] = `${field.label} is required`;
          }
        }
      });
    }

    console.log('[ItemFormDialog] Validation:', { newErrors, id, displayName, hideDocumentId: config.hideDocumentId });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((acc, part) => acc?.[part], obj);
  };

  const persistItem = async (publish) => {
    const itemId = editingItem ? editingItem.id : id.trim();
    const ref = doc(db, config.collectionName, itemId);
    const duplicateMessage = `An item with ID "${itemId}" already exists. Try a different name.`;
    let effectiveData = displayedFormData;
    // A save can precede the selector's asynchronous load. Resolve an unchanged
    // reference again so that opening and saving cannot accidentally retarget it.
    if (needsDataFieldHandling && !fieldSelectionChanged &&
      formData[isStepCollection ? 'outputFieldRef' : 'dataFieldRef']) {
      const snapshot = await getDocs(collection(db, 'documentDataFields'));
      effectiveData = initializeFieldBinding(formData,
        snapshot.docs.map(field => ({ ...field.data(), id: field.id })), isStepCollection);
    }
    const data = {
      ...cleanConfigurationSnapshot(effectiveData),
      id: itemId,
      displayName: displayName.trim(),
      name: displayName.trim(),
      updatedAt: serverTimestamp(),
      updatedBy: currentUser?.uid || currentUser?.email || 'admin',
    };
    if (identityField) data[identityField.key] = identityFieldValue.trim();
    if (needsDataFieldHandling) {
      const fieldKey = isStepCollection ? 'output' : 'dataField';
      data[fieldKey] = data[fieldKey] || selectedUnpairedField?.fieldName || selectedUnpairedField?.id || toSnakeCase(displayName);
    }
    if (!config.disableVersioning) {
      data.version = publish ? (editingItem.version || 1) : (editingItem?.version || 0) + 1;
      data.publishedVersion = publish ? data.version : (editingItem?.publishedVersion || null);
      data.isLocked = publish || editingItem?.isLocked || false;
      data.versions = [
        ...(editingItem?.versions || []).map(cleanConfigurationSnapshot),
        configurationSnapshot(data, {
          version: data.version,
          savedAt: new Date().toISOString(),
          savedBy: currentUser?.uid || currentUser?.email || 'admin',
        }),
      ];
    }
    if (!editingItem) data.createdAt = serverTimestamp();

    if (needsDataFieldHandling) {
      // Keep the separate editors' existing write policy, but make the item
      // and its field registration atomic, including duplicate-create checks.
      await saveConfigurationRevision(db, ref, null, data, {
        create: !editingItem,
        duplicateMessage,
        dataField: {
          fieldName: isStepCollection ? data.output : data.dataField,
          fieldKey: isStepCollection ? 'output' : 'dataField',
          referenceId: isStepCollection ? data.outputFieldRef : data.dataFieldRef,
          displayName: displayName.trim(),
        },
      });
    } else if (editingItem) {
      await updateDoc(ref, data);
    } else {
      if ((await getDoc(ref)).exists()) throw new Error(duplicateMessage);
      await setDoc(ref, data);
    }
    onSave();
  };

  const handleSave = async () => {
    setError("");
    if (!validateForm()) return;
    setSaving(true);
    try {
      await persistItem(false);
    } catch (error) {
      console.error(`Error saving ${config.entityName}:`, error);
      setError("Failed to save: " + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!editingItem) return;
    setError("");
    if (!validateForm()) return;
    setPublishing(true);
    try {
      await persistItem(true);
    } catch (error) {
      console.error(`Error publishing ${config.entityName}:`, error);
      setError("Failed to publish: " + error.message);
    } finally {
      setPublishing(false);
    }
  };

  // Handle unlock confirmation
  const handleUnlockConfirm = () => {
    setSessionUnlocked(true);
    setUnlockDialogOpen(false);
  };
  
  return (
    <>
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth 
      sx={{ zIndex: 'var(--z-modal-backdrop)' }}
      PaperProps={{ sx: { minHeight: '70vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', flexDirection: 'column', pb: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>{editingItem ? `Edit ${config.entityName}` : `Add ${config.entityName}`}</span>
            {editingItem && (
              <Chip 
                label={`v${editingItem.version || 1}`} 
                size="small" 
                variant="outlined"
                sx={{ fontSize: 'var(--text-xs)' }}
              />
            )}
            {isLocked && (
              <Tooltip title="Locked - click to unlock for this session" arrow>
                <IconButton size="small" onClick={() => setUnlockDialogOpen(true)} sx={{ p: 0.5 }}>
                  <Lock size={16} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {hasPriority && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!formData.priority}
                    onChange={(e) => handleFormChange('priority', e.target.checked)}
                    disabled={isLocked}
                  />
                }
                label="Priority Step"
                sx={{ mr: 0 }}
              />
            </Box>
          )}
        </Box>
        {/* Tabs - only show for existing items with versioning enabled */}
        {editingItem && !config.disableVersioning && (
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ minHeight: 36 }}>
            <Tab label="Edit" sx={{ minHeight: 36, py: 0 }} />
            <Tab label="Versions" sx={{ minHeight: 36, py: 0 }} />
          </Tabs>
        )}
      </DialogTitle>
      
      <DialogContent>
        {/* Edit Tab */}
        {activeTab === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Unpaired fields - show when creating new step/section */}
          {needsDataFieldHandling && !editingItem && unpairedFields.length > 0 && !selectedUnpairedField && (
            <Box sx={{ 
              p: 2, 
              bgcolor: 'var(--color-background-alt)', 
              borderRadius: 1,
              border: '1px solid var(--color-border)'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Link2 size={16} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Recent Unpaired Data Fields
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'var(--color-text-secondary)', mb: 1.5 }}>
                {isSectionCollection 
                  ? "These fields have a Step that writes to them but no Section to display them:"
                  : "These fields have a Section that reads them but no Step to populate them:"}
              </Typography>
              <List dense sx={{ bgcolor: 'var(--color-background)', borderRadius: 1, p: 0 }}>
                {unpairedFields.map(field => (
                  <ListItem key={field.id} disablePadding>
                    <ListItemButton 
                      onClick={() => handleUnpairedFieldSelect(field)}
                      sx={{ borderRadius: 1 }}
                    >
                      <ListItemText 
                        primary={field.displayName}
                        secondary={isSectionCollection 
                          ? `From step: ${field.usedBySteps?.join(", ")}`
                          : `Used by: ${field.usedBySections?.join(", ")}`}
                      />
                      <Chip label={field.id} size="small" variant="outlined" />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          
          {/* Identity Fields - Display Name auto-generates ID and identity field */}
          <Box sx={{ 
            p: 2, 
            bgcolor: 'var(--color-background-alt)', 
            borderRadius: 1,
            border: '1px solid var(--color-border)'
          }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
              {selectedUnpairedField ? 'Selected Data Field' : 'Identity'}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {selectedUnpairedField ? (
                // Show selected unpaired field
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{selectedUnpairedField.displayName}</Typography>
                    <Typography variant="body2" sx={{ color: 'var(--color-text-secondary)' }}>
                      Data field: {selectedUnpairedField.id} • {isSectionCollection 
                        ? `From step: ${selectedUnpairedField.usedBySteps?.join(", ")}`
                        : `Used by: ${selectedUnpairedField.usedBySections?.join(", ")}`}
                    </Typography>
                  </Box>
                  <Button size="small" onClick={() => { setSelectedUnpairedField(null); setDisplayName(""); }}>
                    Change
                  </Button>
                </Box>
              ) : (
                // Show display name input
                <TextField
                  label={primaryDisplayField?.label || "Display Name"}
                  value={displayName}
                  onChange={(e) => handleDisplayNameChange(e.target.value)}
                  fullWidth
                  size="small"
                  placeholder={primaryDisplayField?.helpText || `e.g., History of Present Illness`}
                  helperText={errors._displayName}
                  error={!!errors._displayName}
                  autoFocus
                />
              )}
              
              {/* Data field warning */}
              {dataFieldWarning && !selectedUnpairedField && (
                <Alert severity={dataFieldWarning.type} sx={{ py: 0.5 }}>
                  {dataFieldWarning.message}
                </Alert>
              )}
              
              {/* ID field - hidden for steps/sections and when hideDocumentId is set */}
              {!needsDataFieldHandling && !config.hideDocumentId && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="ID (camelCase)"
                    value={id}
                    onChange={(e) => { setId(e.target.value); setIdManuallyEdited(true); }}
                    fullWidth
                    size="small"
                    placeholder={`e.g., historyOfPresentIllness`}
                    helperText={errors._id || (editingItem ? "Cannot be changed" : "Auto-generated")}
                    error={!!errors._id}
                    disabled={!!editingItem}
                    sx={{ flex: 1 }}
                  />
                  {identityField && (
                    <TextField
                      label={identityField.label}
                      value={identityFieldValue}
                      onChange={(e) => { setIdentityFieldValue(e.target.value); setIdentityFieldManuallyEdited(true); }}
                      fullWidth
                      size="small"
                      placeholder={identityField.autoFormat === 'snake_case' ? 'e.g., history_of_present_illness' : ''}
                      helperText="Auto-generated"
                      sx={{ flex: 1 }}
                    />
                  )}
                </Box>
              )}
              
              {/* Identity field for hideDocumentId mode (e.g., Data Fields) */}
              {config.hideDocumentId && identityField && (
                <Box>
                  {editingItem && config.editableIdentityField && !identityFieldUnlocked && (
                    <Alert 
                      severity="warning" 
                      sx={{ mb: 1 }}
                      action={
                        <Button 
                          color="warning" 
                          size="small" 
                          onClick={() => setIdentityFieldUnlocked(true)}
                        >
                          Unlock
                        </Button>
                      }
                    >
                      Changing the field name will break any Steps or Sections that reference it.
                    </Alert>
                  )}
                  <TextField
                    label={identityField.label || "Field Name"}
                    value={identityFieldValue}
                    onChange={(e) => { setIdentityFieldValue(e.target.value); setIdentityFieldManuallyEdited(true); }}
                    fullWidth
                    size="small"
                    placeholder={identityField.autoFormat === 'snake_case' ? 'e.g., chief_complaint' : ''}
                    helperText={errors._identityField || (editingItem && !identityFieldUnlocked ? "Locked - click Unlock to edit" : "The snake_case identifier used in code")}
                    error={!!errors._identityField}
                    disabled={editingItem && config.editableIdentityField && !identityFieldUnlocked}
                  />
                </Box>
              )}
            </Box>
          </Box>

          {/* Schema-driven form */}
          {schema ? (
            <Box sx={{ mt: 1 }}>
              <SchemaFormRenderer
                schema={schema}
                data={displayedFormData}
                onChange={handleFormChange}
                errors={errors}
                excludeFields={[
                  ...(hasPriority ? ['priority'] : []), // Priority shown in header
                  ...(identityField ? [identityField.key] : []),  // Identity field shown in identity section
                  ...(primaryDisplayFieldKey ? [primaryDisplayFieldKey] : []),  // Primary display field shown in identity section
                  ...(schema?.fields?.filter(f => f.hidden).map(f => f.key) || [])  // Hidden fields (e.g., output, dataField)
                ]}
              />
            </Box>
          ) : (
            <Alert severity="info">
              Editor unavailable.
            </Alert>
          )}

          {error && (
            <Alert severity="error">{error}</Alert>
          )}
        </Box>
        )}
        
        {/* Versions Tab */}
        {activeTab === 1 && editingItem && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Version History
            </Typography>
            {editingItem.versions?.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[...editingItem.versions].reverse().map((ver, idx) => {
                  const isPublished = editingItem.publishedVersion === ver.version;
                  const savedDate = ver.savedAt ? new Date(ver.savedAt).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit"
                  }) : "Unknown date";
                  
                  return (
                    <Box 
                      key={`${ver.version}-${editingItem.versions.length - 1 - idx}`}
                      sx={{ 
                        p: 1.5, 
                        border: '1px solid var(--color-border)',
                        borderRadius: 1,
                        bgcolor: isPublished ? 'var(--color-success-light)' : 'transparent'
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Version {ver.version}
                          </Typography>
                          {isPublished && (
                            <Chip label="Published" size="small" color="success" />
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {savedDate}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Typography color="text.secondary">No version history available.</Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {editingItem && hasUnpublishedChanges && !config.disableVersioning && (
          <Button 
            onClick={handlePublish} 
            variant="outlined" 
            color="success"
            disabled={publishing}
            startIcon={<Send size={16} />}
          >
            {publishing ? "Publishing..." : "Publish"}
          </Button>
        )}
        <Button 
          onClick={isLocked ? () => setUnlockDialogOpen(true) : handleSave} 
          variant="contained" 
          disabled={saving}
          startIcon={isLocked ? <Lock size={16} /> : null}
        >
          {saving ? "Saving..." : isLocked ? "Unlock" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
    
    {/* Unlock Confirmation Dialog - separate from main dialog to avoid nesting issues */}
    <Dialog open={unlockDialogOpen} onClose={() => setUnlockDialogOpen(false)} sx={{ zIndex: 10002 }}>
      <DialogTitle>Unlock for Editing?</DialogTitle>
      <DialogContent>
        <Typography>
          This {config.entityName.toLowerCase()} is locked because it has been published. 
          Unlocking will allow you to make changes for this session only.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setUnlockDialogOpen(false)}>Cancel</Button>
        <Button onClick={handleUnlockConfirm} variant="contained" startIcon={<Unlock size={16} />}>
          Unlock
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default ItemFormDialog;
